/**
 * Home page content — ported VERBATIM from the Claude Design artboard
 * `biz_in_a_folder_tommillerservices/clients/tommillerservices/brand/design/Home.dc.html`
 * (Tom, 2026-09-02). Copy changes go there first, then here the same day.
 *
 * Prices on this page must agree with
 * `clients/tommillerservices/brand/message-consistency.md` (the published list).
 *
 * The ownership LADDER is deliberately NOT on this page (2026-09-06). It was —
 * twice, in two overlapping grids — and it is also on /ownership and in
 * /terms §2. The home page carries the one-sentence version and hands off.
 * `publicLegalPages.test.ts` pins this.
 *
 * MEMBER-ONLY copy (2026-09-06, Tom): the BB token band and every sentence of
 * investment language — "investment opportunity", "profit distributions",
 * "ground floor", "tokenized investment" — render only for a logged-in
 * visitor. Offering a token with distributions and governance rights to the
 * public is financial-product language in Australia, and neither /terms nor
 * /privacy covers the token. Anything marked `memberOnly` or living under a
 * `.member` key is filtered out for the logged-out page; the test pins that
 * the public copy carries none of those words.
 */

export interface HomeProblemCard {
  title: string;
  body: string;
  /** The last card is the accented "invest" card in the design. */
  highlight?: boolean;
  /** Rendered only for a logged-in visitor (investment language). */
  memberOnly?: boolean;
}

export interface HomeDiscipline {
  number: string;
  title: string;
  body: string;
  points: string[];
  /** The mono tag line under the card ("REACT · GRAPHQL · NODE.JS"). */
  tags: string;
  highlight?: boolean;
}

export interface HomeModuleCard {
  icon: string;
  title: string;
  body: string;
  points: string[];
}

export interface HomeSystemCard {
  kicker: string;
  title: string;
  body: string;
  points: string[];
}

export interface HomeChainCard {
  title: string;
  body: string;
  points: string[];
}

export interface HomeExtra {
  title: string;
  body: string;
}

export interface HomeEvent {
  time: string;
  place: string;
  title: string;
  body: string;
  /** Rendered only for a logged-in visitor (investment language). */
  memberOnly?: boolean;
}

export interface HomeSocialPillar {
  title: string;
  body: string;
}

export interface HomeTier {
  icon: string;
  name: string;
  price: string;
  blurb: string;
  points: string[];
}

export interface HomeOwnershipNote {
  heading: string;
  body: string;
  /** The line that closes most ownership conversations (message-consistency.md). */
  closer: string;
}

export interface HomeProgramPhase {
  phase: string;
  title: string;
  body: string;
  stages: string[];
}

export interface HomeTokenStat {
  title: string;
  body: string;
}

export interface HomeProofStep {
  step: string;
  title: string;
  body: string;
  /** Steps 01/02 are in production; 03/04 are the research direction. Never let the page imply otherwise. */
  shipped: boolean;
}

/* ------------------------------------------------------------------ */
/* Contact details (the real ones)                                     */
/* ------------------------------------------------------------------ */

export const CONTACT = {
  phoneDisplay: "+61 458 944 327",
  phoneHref: "tel:+61458944327",
  email: "tom@tommillerservices.com",
  emailHref: "mailto:tom@tommillerservices.com",
  /** Every "Book a free consultation" / "book a call" CTA opens Tom's public booking page in a new tab (Tom, 2026-09-03). */
  bookingPath: "/book/tom-miller",
  /** The Business Builder product page (offer, interest form). */
  consultationPath: "/businessbuilder",
  legalLine: "TOM MILLER SERVICES · DR THOMAS MILLER · ABN 84 844 361 358 · BRISBANE, QUEENSLAND",
} as const;

/** Static count shown until (or if) the live `availableModules` query answers. */
export const MODULE_COUNT_FALLBACK = 81;

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */

export const HERO_PROOF: string[] = [
  "PhD Blockchain Security · QUT",
  "10+ Years Shipping",
  "Brisbane, Queensland",
  "Dozens of live tenants",
];

/* ------------------------------------------------------------------ */
/* #problem — The disconnect                                           */
/* ------------------------------------------------------------------ */

/** The second lead paragraph of #problem — the public version names no token. */
export const PROBLEM_LEAD = {
  public: "Business Builder gives you infrastructure you control, AI you train, and a published price for every exit.",
  member: "Business Builder gives you infrastructure you control, AI you train, and the ability to participate in the platform's growth through tokenized investment.",
} as const;

export const PROBLEM_CARDS: HomeProblemCard[] = [
  { title: "Own your data", body: "Your business knowledge stays yours, and it is always yours to export." },
  { title: "Train your AI", body: "Custom AI learns from your business and represents your interests, not Google's or Facebook's." },
  { title: "Scale sovereignly", body: "Grow without vendor lock-in or data exploitation. Every exit has a published price." },
  { title: "Invest in the platform", body: "Participate in the growth through tokenized ownership and monthly distributions.", highlight: true, memberOnly: true },
];

/* ------------------------------------------------------------------ */
/* #platform — Three disciplines, one stack                            */
/* ------------------------------------------------------------------ */

export const DISCIPLINES: HomeDiscipline[] = [
  {
    number: "01",
    title: "Custom website development",
    body: "Modern, responsive websites built with the latest technologies, tailored to your business and tuned for performance.",
    points: [
      "React frontend with beautiful UI components",
      "GraphQL API for flexible data handling",
      "Mobile-responsive across all screen sizes",
      "Fast deployment with continuous updates",
    ],
    tags: "REACT · GRAPHQL · NODE.JS",
  },
  {
    number: "02",
    title: "Blockchain security & integration",
    body: "From secure crypto payments to transparent supply chains, distributed ledger technology applied where it earns its place.",
    points: [
      "Crypto wallet integration for secure transactions",
      "Custom token development and deployment",
      "Web3 implementation for decentralized apps",
      "Hardware wallet setup and security training",
    ],
    tags: "SMART CONTRACTS · TOKENIZATION · SECURITY",
  },
  {
    number: "03",
    title: "AI training & custom agents",
    body: "Intelligent agents that automate workflows, sharpen decision-making and answer customers around the clock.",
    points: [
      "Custom AI agents for business automation",
      "Specialized models trained on your data",
      "Chatbots and virtual assistants",
      "Integration with Claude, GPT and open models",
    ],
    tags: "CUSTOM MODELS · AGENTS · AUTOMATION",
    highlight: true,
  },
];

/* ------------------------------------------------------------------ */
/* #modules — Business module suite                                    */
/* ------------------------------------------------------------------ */

export const MODULES: HomeModuleCard[] = [
  { icon: "👥", title: "Client Management", body: "Profiles, communication history and document storage. Track interactions and maintain detailed records.", points: ["Client profiles and contact management", "Secure document storage", "Communication tracking"] },
  { icon: "📅", title: "Booking Calendar", body: "Availability management, session types and automated reminders for consultations, events or services.", points: ["Customizable session types", "SMS & email reminders", "Integrated payment processing"] },
  { icon: "🛒", title: "Product Marketplace", body: "A complete e-commerce solution for listing and selling products, from catalogue to fulfilment.", points: ["Product listings with images", "Shopping cart functionality", "Order management"] },
  { icon: "💳", title: "Billing System", body: "Configurable billing that integrates with your accounting software. Invoice, track and report.", points: ["Invoice generation", "Payment tracking", "Accounting software integration"] },
  { icon: "📊", title: "Project Management", body: "Client-facing project tracking with tasks, progress and transparent shared updates.", points: ["Task management", "Progress tracking", "File uploads and evidence"] },
  { icon: "🔗", title: "Supply Chain Tracking", body: "Blockchain-based tracking to enhance transparency, reduce fraud and improve logistics efficiency.", points: ["Immutable product tracking", "Transparent supply chain", "QR code integration"] },
];

/* ------------------------------------------------------------------ */
/* #systems — Map it, build it, watch it run                           */
/* ------------------------------------------------------------------ */

export const SYSTEMS: HomeSystemCard[] = [
  { kicker: "VoIP & mobile integration", title: "Digital Phone System", body: "Complete VoIP with mobile integration, call routing, voicemail transcription and CRM sync.", points: ["VoIP with mobile app integration", "AI-powered call transcription", "Smart call routing & IVR", "CRM & calendar integration"] },
  { kicker: "Workflow optimization", title: "Process Mapping", body: "Visual business process mapping with bottleneck detection and AI-analysed recommendations.", points: ["Interactive process diagrams", "Bottleneck identification", "Automation opportunities", "KPI tracking & reporting"] },
  { kicker: "End-to-end execution", title: "Process Implementation", body: "Turn mapped processes into reality with automated workflows and team training modules.", points: ["Workflow automation setup", "Team training & onboarding", "Change management support", "Performance monitoring"] },
  { kicker: "Real-time analytics", title: "Process Tracking", body: "Live process monitoring with dashboards, alerts and predictive analytics for compliance.", points: ["Live process dashboards", "Automated alert system", "Compliance tracking", "Predictive analytics"] },
  { kicker: "Digital life manager", title: "AI Calendar Assistant", body: "Calendar management that learns your preferences, auto-schedules meetings and manages priorities.", points: ["Smart meeting scheduling", "Priority management AI", "Time blocking optimization", "Cross-platform sync"] },
  { kicker: "Appointment intelligence", title: "Smart Booking System", body: "Automatic scheduling, resource allocation, payment processing and follow-up automation.", points: ["Smart availability matching", "Resource & room booking", "Integrated payments", "Automated follow-ups"] },
];

/* ------------------------------------------------------------------ */
/* #chain — Transparency you can audit                                 */
/* ------------------------------------------------------------------ */

export const CHAIN: HomeChainCard[] = [
  { title: "Secure Video Server", body: "End-to-end encrypted conferencing with blockchain verification for sensitive communications.", points: ["End-to-end encryption", "Self-hosted solution", "Blockchain-verified identity", "Screen sharing and recording"] },
  { title: "Crypto Platform Deployment", body: "Custom token creation and cryptocurrency platform development, from ERC-20 to full ecosystems.", points: ["ERC-20/ERC-721 token creation", "Smart contract development", "Custom blockchain solutions", "Multi-chain deployment"] },
  { title: "DAO Governance Tools", body: "Transparent decision-making, voting and accountability for distributed teams and communities.", points: ["Transparent proposal systems", "Secure voting mechanisms", "Token-based governance", "Multi-sig treasury management"] },
];

export const EXTRAS: HomeExtra[] = [
  { title: "SMS Integration", body: "Automated notifications and reminders for appointments, updates and campaigns." },
  { title: "Email Systems", body: "Custom email servers and notification systems for reliable business communications." },
  { title: "Sales Funnels", body: "Marketing funnels with lead capture, nurturing and conversion tracking." },
  { title: "Governance Tools", body: "Transparent organizational tools for decision-making, voting and accountability." },
  { title: "Self-Hosting", body: "Secure, tenant-based self-hosting for complete data sovereignty." },
  { title: "Crypto Payments", body: "Cryptocurrency payment options with flexible processing and conversion." },
];

/* ------------------------------------------------------------------ */
/* #stateofplay — static fallback when no published episode is public  */
/* ------------------------------------------------------------------ */

export const EVENTS: HomeEvent[] = [
  { time: "LIVE", place: "GLOBAL", title: "Governance module opens to all tenants", body: "Proposal creation and Snapshot voting available on every Professional plan and above." },
  { time: "THIS WEEK", place: "BRISBANE", title: "Private IPFS storage goes multi-region", body: "Media CDN and Vault Insights now replicate across three nodes." },
  { time: "THIS MONTH", place: "QUT", title: "Supply chain paper accepted", body: "Third publication in collaboration with Queensland University of Technology." },
  { time: "NEXT", place: "PLATFORM", title: "BB distribution round", body: "Monthly profit distribution snapshot taken for all token holders.", memberOnly: true },
];

export const SOP_PILLS: string[] = ["Placed & timestamped", "Signed on-chain", "Community authored"];

/* ------------------------------------------------------------------ */
/* #social — A new social layer                                        */
/* ------------------------------------------------------------------ */

export const SOCIAL_PILLARS: HomeSocialPillar[] = [
  { title: "Governance", body: "Snapshot voting, transparent proposals, multi-sig treasury" },
  { title: "Sovereignty", body: "Self-hosting, private IPFS, your data always exportable" },
  { title: "Cooperation", body: "Shared infrastructure where a feature built for one benefits everyone" },
];

/* ------------------------------------------------------------------ */
/* #pricing — Subscription plans (message-consistency.md is the SSOT)  */
/* ------------------------------------------------------------------ */

export const TIERS: HomeTier[] = [
  { icon: "🌱", name: "Foundation", price: "$97/mo", blurb: "Track your pipeline, build your business.", points: ["Professional website", "Client CRM", "Opportunity tracker + coach", "Pipeline management"] },
  { icon: "🚀", name: "Growth", price: "$297/mo", blurb: "Start billing and learning.", points: ["Everything in Foundation", "Billing & invoicing", "Learning management", "Product catalog"] },
  { icon: "🛡️", name: "Growth+", price: "$497/mo", blurb: "Complete managed hosting, security and monthly feature development.", points: ["Everything in Growth", "Managed hosting & security programme", "Monthly feature development", "Quarterly posture review"] },
  { icon: "💼", name: "Professional", price: "$597/mo", blurb: "Complete business suite. Most popular.", points: ["Everything in Growth", "Calendars + broadcasts", "Proposals & e-signing", "Knowledge base + analytics"] },
  { icon: "⚡", name: "Advanced", price: "From $897/mo", blurb: "For teams that need the whole platform.", points: ["Communications — voice, SMS, video ($897)", "Intelligence — AI automation ($1,497)", "Enterprise — multi-site + AI editor ($2,997)", "Dominance — hosted white-label ($5,997)"] },
];

/** The one-sentence version. The ladder itself lives on /ownership and in /terms §2. */
export const OWNERSHIP_NOTE: HomeOwnershipNote = {
  heading: "Every exit has a published price",
  body:
    "Every plan above is a managed service on a shared platform — hosted, patched, monitored and improved by TMS. No monthly plan at any price converts into the source code. Owning it is a separate purchase with a published price, from a skeleton build at $100,000 to a data centre of your own, and there is one price list that every client sits on. Leaving is free: a full copy of your data, any time, no reason needed — a right that survives a dispute, an unpaid invoice, a termination, and the wind-up of TMS itself.",
  closer:
    "If you want the code, buy the rung. If what you actually want is certainty that you can leave, you already have it, and it is free.",
};

/* ------------------------------------------------------------------ */
/* #program — The Program                                              */
/* ------------------------------------------------------------------ */

export const PROGRAM_PHASES: HomeProgramPhase[] = [
  { phase: "Phase one", title: "A private invite", body: "No public funnel. An invite link is sent to one person, exchanged for a real session, and lands them on the vision pages.", stages: ["Invited", "Vision viewed"] },
  { phase: "Phase two", title: "Skin in the game", body: "The application pack is paid before the questionnaire opens — financial capacity, willingness, the business, proof of assets.", stages: ["Application paid", "Qualifying submitted"] },
  { phase: "Phase three", title: "Two real calls", body: "Booked into a real calendar with a meeting room, against a call SLA. The wow page sits between them.", stages: ["Call 1", "Wow page viewed", "Call 2"] },
  { phase: "Phase four", title: "Sign, deposit, build", body: "A tier is chosen, the agreement is signed and hash-pinned, the down-payment bill goes out, then the build starts — ending on the mentor track, reselling under their own brand.", stages: ["Offer sent", "Down-payment paid", "Onboarded", "Mentor track"] },
];

/* ------------------------------------------------------------------ */
/* #token — Investment opportunity                                     */
/* ------------------------------------------------------------------ */

export const TOKEN_STATS: HomeTokenStat[] = [
  { title: "Earn it", body: "Contribution to the platform is recognised in BB." },
  { title: "Buy it", body: "Purchase directly and join the ground floor." },
  { title: "Use it", body: "Redeem against your monthly bill as a discount." },
  { title: "Vote with it", body: "Transparent governance through Snapshot." },
];

/* ------------------------------------------------------------------ */
/* #proof — Where this is going                                        */
/* ------------------------------------------------------------------ */

export const PROOF: HomeProofStep[] = [
  { step: "01", title: "Today — documents", body: "We agree in prose. Terms, proposals, invoices and signatures, each one read and interpreted by a person on both sides.", shipped: true },
  { step: "02", title: "Next — shared schemas", body: "The agreement stops being prose and becomes a schema both parties hold. Same fields, same meanings, no reconciliation call.", shipped: true },
  { step: "03", title: "Then — attested state", body: "Each change of state is signed and anchored on-chain, so the record of what was agreed is verifiable rather than asserted.", shipped: false },
  { step: "04", title: "The goal — formal proof", body: "Schemas carry proofs. Being on the same page becomes something a machine can check, not something we hope is true.", shipped: false },
];
